import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { TagEditor } from '../../components/common/TagEditor';
import { LABEL_SOURCE_STYLES } from '../../constants';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalGtSample, LABEL_SOURCE_LABELS } from '../../types';
import { fmtIso } from '../../utils';

export const DatasetCreatePage: React.FC = () => {
  const { token } = useAuthContext();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  const [unassigned, setUnassigned] = useState<EvalGtSample[]>([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filterStoreId, setFilterStoreId] = useState('');
  const [filterChainId, setFilterChainId] = useState('');
  const [filterIngestedBy, setFilterIngestedBy] = useState('');

  const loadSamples = useCallback(() => {
    setLoadingUnassigned(true);
    EvalApiService.listGtSamples(token ?? '', {
      unassigned: true,
      store_id: filterStoreId ? Number(filterStoreId) : undefined,
      chain_id: filterChainId ? Number(filterChainId) : undefined,
      ingested_by: filterIngestedBy || undefined,
    })
      .then(({ items }) => setUnassigned(items))
      .catch(() => {})
      .finally(() => setLoadingUnassigned(false));
  }, [token, filterStoreId, filterChainId, filterIngestedBy]);

  useEffect(() => {
    loadSamples();
  }, [loadSamples]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const dataset = await EvalApiService.createDataset(token ?? '', {
        dataset_name: name.trim(),
        description: desc.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
      });
      if (selected.size > 0) {
        await EvalApiService.addInferencesToDataset(token ?? '', dataset.dataset_id, {
          gt_sample_ids: Array.from(selected),
        });
      }
      navigate(`/eval/datasets/${dataset.dataset_id}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">New Dataset</div>
          <div className="eval-page-sub">
            Create a ground-truth dataset and optionally add samples.
          </div>
        </div>
        <button
          className="eval-btn-ghost"
          onClick={() => navigate('/eval/datasets')}
          disabled={creating}
        >
          Cancel
        </button>
      </div>

      <form onSubmit={handleCreate}>
        {/* Metadata */}
        <div className="eval-inline-form-panel" style={{ maxWidth: 540, marginBottom: 28 }}>
          <div className="eval-form">
            <div className="eval-form-group">
              <label className="eval-form-label">Dataset Name</label>
              <input
                className="eval-form-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. shelf-edge-cases-v3"
                required
                disabled={creating}
              />
            </div>
            <div className="eval-form-group">
              <label className="eval-form-label">Description (optional)</label>
              <textarea
                className="eval-form-textarea"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="Describe the dataset purpose and collection method..."
                disabled={creating}
              />
            </div>
            <div className="eval-form-group">
              <label className="eval-form-label">Tags (optional)</label>
              <TagEditor
                token={token ?? ''}
                tags={tags}
                onChange={(t) => setTags(t)}
                disabled={creating}
              />
            </div>
          </div>
        </div>

        {/* Sample picker */}
        <div style={{ marginBottom: 80 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              Samples (optional)
              {selected.size > 0 && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 12,
                    color: 'var(--accent)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {selected.size} selected
                </span>
              )}
            </div>
            <input
              className="eval-form-input"
              placeholder="store_id"
              value={filterStoreId}
              onChange={(e) => setFilterStoreId(e.target.value.replace(/\D/g, ''))}
              style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
            <input
              className="eval-form-input"
              placeholder="chain_id"
              value={filterChainId}
              onChange={(e) => setFilterChainId(e.target.value.replace(/\D/g, ''))}
              style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
            <input
              className="eval-form-input"
              placeholder="ingested by"
              value={filterIngestedBy}
              onChange={(e) => setFilterIngestedBy(e.target.value)}
              style={{ width: 160, fontSize: 12 }}
            />
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {loadingUnassigned ? 'Loading…' : `${unassigned.length} available`}
            </span>
          </div>

          <div className="eval-inference-grid">
            {unassigned.map((inf) => (
              <div
                key={inf.gt_sample_id}
                className="eval-inference-thumb"
                onClick={() => toggleSelect(inf.gt_sample_id)}
                style={{
                  cursor: 'pointer',
                  outline: selected.has(inf.gt_sample_id) ? '2px solid var(--accent)' : undefined,
                }}
              >
                <div className="eval-inference-thumb-img">
                  {inf.image_url ? (
                    <img
                      src={inf.image_url}
                      alt={`image_id: ${inf.image_id}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : inf.image_id != null ? (
                    `image_id: ${inf.image_id}`
                  ) : (
                    inf.gt_sample_id
                  )}
                </div>
                <div className="eval-inference-thumb-meta">
                  <div style={{ marginBottom: 2, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                    store: {inf.store_id ?? '—'} · chain: {inf.chain_id ?? '—'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 2 }}>
                    {fmtIso(inf.ingested_at)}
                  </div>
                  {inf.ingested_by && (
                    <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 2 }}>
                      by {inf.ingested_by.split('@')[0]}
                    </div>
                  )}
                  <span style={LABEL_SOURCE_STYLES[inf.label_source]}>
                    {LABEL_SOURCE_LABELS[inf.label_source]}
                  </span>
                </div>
              </div>
            ))}
            {!loadingUnassigned && unassigned.length === 0 && (
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, gridColumn: '1/-1' }}>
                No unassigned inferences available.
              </div>
            )}
          </div>
        </div>

        {/* Sticky footer */}
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: 'var(--surface)',
            borderTop: '1px solid var(--border)',
            padding: '12px 0',
            marginTop: 8,
          }}
        >
          <button type="submit" className="eval-btn" disabled={creating || !name.trim()}>
            {creating
              ? 'Creating…'
              : selected.size > 0
                ? `Create with ${selected.size} sample${selected.size !== 1 ? 's' : ''}`
                : 'Create Dataset'}
          </button>
        </div>
      </form>
    </div>
  );
};
